// src/components/configuration/sections/TeamSectionShadcn.jsx - Pure shadcn implementation
import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Shield,
  Users,
  Mail
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';

// Keep your existing PageContainer for headers
import PageContainer from '../../ui/layout/PageContainer/PageContainer';

// Import the shadcn split panel components
import TeamMembersListShadcn from '../../ui/sections/TeamMembersList/TeamMembersListShadcn';
import TeamMemberEditorShadcn from '../../ui/sections/TeamMemberEditor/TeamMemberEditorShadcn';

// Keep your existing hook - same data pipeline!
import { useTeam } from '../../../hooks/useTeam';

const TeamSectionShadcn = () => {
  // Get real data from team context - EXACT same hook as before
  const {
    teamMembers,
    permissions,
    pendingInvitations,
    currentUser,
    updateMemberRole,
    inviteTeamMember,
    cancelInvitation,
    resendInvitation,
    removeTeamMember,
    reactivateTeamMember,
    loading,
    permissionsLoading,
    roleUpdateLoading,
    inviteLoading,
    invitationsLoading,
    error,
    isConnected,
    loadTeamMembers,
    loadPermissions,
    clearError,
    refreshTeamData,
    // Permission helpers - same as before
    canManageTeam,
    canAddTeamMembers,
    canUpdateTeamRoles,
    canRemoveTeamMembers,
    canManageInvitations,
    currentRole,
    // Team stats - same as before
    totalMembers,
    pendingInvitationsCount,
    adminMembers,
    managerMembers,
    staffMembers
  } = useTeam();

  // Local UI state for split panel - same as before
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState('active'); // 'all', 'active', 'inactive'

  // Auto-select first member when team loads - same as before
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0 && !selectedMemberId) {
      setSelectedMemberId(teamMembers[0].id);
    }
  }, [teamMembers, selectedMemberId]);

  // Get selected member object - same as before
  const selectedMember = selectedMemberId ?
    teamMembers.find(member => member.id === selectedMemberId) : null;

  // Handle member operations - same logic as before!
  const handleUpdateMember = (memberId, updateData) => {
    console.log('👤 Updating team member:', memberId, updateData);
    // Note: updateMember functionality would need to be added to TeamProvider
    // For now, we'll focus on role updates which are implemented
    // Success/error handling is managed by the provider
  };

  const handleUpdateRole = (memberId, newRole) => {
    console.log('🔄 Updating member role:', memberId, '→', newRole);
    updateMemberRole(memberId, newRole);
    // Success/error handling is managed by the provider
  };

  const handleInviteMember = (email, role, message) => {
    console.log('📧 Inviting team member:', email, 'as', role);
    inviteTeamMember(email, role, message);
    // Success/error handling is managed by the provider
  };

  const handleCancelInvitation = (invitationId) => {
    console.log('🚫 Cancelling invitation:', invitationId);
    cancelInvitation(invitationId);
    // Success/error handling is managed by the provider
  };

  const handleResendInvitation = (invitationId) => {
    console.log('🔄 Resending invitation:', invitationId);
    resendInvitation(invitationId);
    // Success/error handling is managed by the provider
  };

  const handleRemoveMember = (memberId) => {
    console.log('🗑️ Removing team member:', memberId);
    removeTeamMember(memberId, ''); // reason is optional, can add UI for it later
    // Success/error handling is managed by the provider
  };

  const handleReactivateMember = (memberId) => {
    console.log('🔄 Reactivating team member:', memberId);
    reactivateTeamMember(memberId);
    // Success/error handling is managed by the provider
  };

  // Check permissions for access - same as before
  if (!canManageTeam) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center p-8 bg-muted/30 border border-border rounded-2xl">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Access Restricted
            </h2>
            <p className="text-muted-foreground mb-4">
              Only administrators can access this section.
            </p>
            <div className="text-sm text-muted-foreground">
              Your current role: <span className="font-medium text-foreground">{currentRole || 'Unknown'}</span>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Loading state - now using shadcn styling
  if (loading || permissionsLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading your team...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving team members and permissions</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Handle errors - now using shadcn Alert
  if (error) {
    return (
      <PageContainer>
        <div className="max-w-6xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Team Error</p>
                  <p className="text-sm">{error}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    clearError();
                    refreshTeamData();
                  }}
                >
                  Retry Loading
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport">
      {/* Error Message - Provider handles success/error states */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={clearError}
              >
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="grid grid-cols-12 gap-4 h-full">
        {/* Left Panel - Team Members List (33% width) */}
        <div className="col-span-4 h-full min-h-0">
          <TeamMembersListShadcn
            teamMembers={teamMembers}
            pendingInvitations={pendingInvitations}
            selectedMemberId={selectedMemberId}
            onSelectMember={setSelectedMemberId}
            onInviteMember={handleInviteMember}
            onCancelInvitation={handleCancelInvitation}
            onResendInvitation={handleResendInvitation}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            activeStatusFilter={activeStatusFilter}
            onActiveStatusFilterChange={setActiveStatusFilter}
            loading={loading || inviteLoading}
            canInviteMembers={canAddTeamMembers}
            canManageInvitations={canManageInvitations}
          />
        </div>

        {/* Right Panel - Team Member Editor (67% width) */}
        <div className="col-span-8 h-full min-h-0">
          <TeamMemberEditorShadcn
            member={selectedMember}
            currentUser={currentUser}
            onUpdateMember={handleUpdateMember}
            onUpdateRole={handleUpdateRole}
            onRemoveMember={handleRemoveMember}
            onReactivateMember={handleReactivateMember}
            permissions={{
              canManageTeam,
              canUpdateTeamRoles,
              canRemoveTeamMembers
            }}
            savingMember={false} // Will be implemented when member update is added
            savingRole={roleUpdateLoading}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default TeamSectionShadcn;